import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { showError, showSuccess } from "../../../../util/ShowStatus";
import { convertDateToEpochUTC } from "../../../../helper/DateConverters";
import { getErrorMessage } from "../../../../helper/getResponseMessage";
import axios from "../../../../util/axiosconfig";
import PhotoUpload from "../Job Sheet Belfast/photoUpload";
import {
  fetchProjectNameMasterList,
  fetchObservationTypeMasterList,
  fetchAmelioDepartmentMasterList,
} from "./fetchData";

interface AddObserveInformReportProps {
  isPublic?: boolean;
}

const AddObserveInformReport: React.FC<AddObserveInformReportProps> = ({
  isPublic = false,
}) => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const [projectNameOptions, setProjectNameOptions] = useState<any[]>([]);
  const [observationTypeOptions, setObservationTypeOptions] = useState<any[]>(
    [],
  );
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);

  const [formValues, setFormValues] = useState<any>({
    date: convertDateToEpochUTC(new Date(new Date().setHours(0, 0, 0, 0))),
    name: isPublic ? null : localStorage.displayName,
    siteLocation: null,
    project: null,
    observation: null,
    department: null,
    description: null,
    controlMeasures: null,
    submittedBy: {
      displayName: localStorage.getItem("displayName") || null,
      userID: localStorage.getItem("userID") || null,
    },
  });

  // Photo upload states - same pattern as VehicleAssessment
  const [images, setImages] = useState<Record<string, File>>({});
  const [isUploadInProgress, setIsUploadInProgress] = useState<boolean>(false);
  const [fileValues, setFileValues] = useState<any[]>([]);
  const [timeStampLocationValues, setTimeStampLocationValues] = useState({
    locations: [],
    timeStamps: [],
  });

  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  useEffect(() => {
    fetchProjectNameMasterList(setProjectNameOptions, toast);
    fetchObservationTypeMasterList(setObservationTypeOptions, toast);
    fetchAmelioDepartmentMasterList(setDepartmentOptions, toast);
  }, []);

  const validateForm = (): boolean => {
    if ((!formValues.name || formValues.name.trim() === "") && isPublic) {
      showError("Name is required.", toast);
      return false;
    }

    if (!formValues.date) {
      showError("Date is required.", toast);
      return false;
    }
    if (!formValues.department) {
      showError("Please select an Amelio Department.", toast);
      return false;
    }
    if (!formValues.project) {
      showError("Please select an Observation Type.", toast);
      return false;
    }
    if (!formValues.siteLocation || formValues.siteLocation.trim() === "") {
      showError("Site Name & Location is required.", toast);
      return false;
    }
    if (!formValues.observation) {
      showError("Please select an Observation value.", toast);
      return false;
    }
    if (!formValues.description || formValues.description.trim() === "") {
      showError("Description is required.", toast);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    try {
      if (!validateForm()) {
        return;
      }
      setFormSubmitting(true);
      setIsUploadInProgress(true);

      // Prepare FormData - same pattern as VehicleAssessment
      const formData = new FormData();
      const photosArray: File[] = [];

      fileValues.forEach((photoItem: any) => {
        if (photoItem?.photoKey && photoItem?.file) {
          const fileName = `${photoItem.photoKey}.png`;
          const dataURL = new Blob([photoItem.file], {
            type: "image/png",
          });
          const photoFile = new File([dataURL], fileName, {
            type: "image/png",
          });
          photosArray.push(photoFile);
        }
      });

      photosArray.forEach((file) => {
        formData.append("photos", file);
      });

      formData.append("formJson", JSON.stringify(formValues));
      formData.append(
        "timeStampLocationJson",
        JSON.stringify(timeStampLocationValues),
      );
      formData.append("isWeb", "true");
      formData.append("departmentID", String(formValues.department?.id || ""));

      const response = await axios.post(
        `health-and-safety/observe-inform-report-form${isPublic ? "-public" : ""}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setFormSubmitting(false);
      setIsUploadInProgress(false);
      showSuccess(
        response.data.Message || "Form submitted successfully",
        toast,
      );

      if (isPublic) {
        setSubmissionSuccess(true);
      } else {
        navigate("/layout/health-and-safety/observe-inform-report");
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setFormSubmitting(false);
      setIsUploadInProgress(false);
      showError(errorMessage, toast);
    }
  };

  const handleCancel = () => {
    if (isPublic) {
      // Reset form for public users
      resetForm();
    } else {
      navigate("/layout/health-and-safety/observe-inform-report");
    }
  };

  const resetForm = () => {
    setFormValues({
      date: convertDateToEpochUTC(new Date(new Date().setHours(0, 0, 0, 0))),
      name: null,
      siteLocation: null,
      project: null,
      observation: null,
      department: null,
      description: null,
      controlMeasures: null,
      submittedBy: {
        displayName: localStorage.getItem("displayName") || null,
        userID: localStorage.getItem("userID") || null,
      },
    });
    setImages({});
    setFileValues([]);
    setTimeStampLocationValues({ locations: [], timeStamps: [] });
    setSubmissionSuccess(false);
  };

  const isDisabled = isUploadInProgress || formSubmitting;

  // Success message for public submission
  if (submissionSuccess && isPublic) {
    return (
      <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-p-4">
        <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8 tw-max-w-md tw-text-center">
          <div className="tw-w-16 tw-h-16 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
            <i className="pi pi-check tw-text-green-600 tw-text-3xl"></i>
          </div>
          <h2 className="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-2">
            Thank You!
          </h2>
          <p className="tw-text-gray-600 tw-mb-6">
            Your observation report has been submitted successfully.
          </p>
          <Button
            label="Submit Another Report"
            className="tw-bg-primary tw-border-primary tw-text-white tw-px-6 tw-py-2"
            onClick={resetForm}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <div
        className={`tw-border-solid tw-border tw-border-[#E2E2e2] tw-rounded-lg tw-relative tw-bg-white ${
          isPublic ? "tw-max-w-4xl tw-mx-auto tw-shadow-lg" : ""
        }`}
      >
        <div className="tw-bg-[#F8FAFC] tw-p-4 tw-rounded-t-lg tw-border-b-[1px] tw-border-[#E2E8F0] tw-flex tw-justify-between tw-items-center">
          <h2 className="tw-text-lg sm:tw-text-xl xl:tw-text-2xl tw-font-semibold tw-text-[#101828] tw-text-center tw-w-full">
            Observe. Inform. Report.
          </h2>
        </div>
        <div>
          <div className="tw-p-2 sm:tw-p-4">
            {/* Basic Details */}
            <div className="tw-border-b-[1px] tw-border-[#E2E8F0] tw-mb-2 sm:tw-mb-4">
              {/* <h3 className="tw-text-base tw-font-semibold tw-text-[#101828] tw-mb-4">
                Report Details
              </h3> */}
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-6 tw-w-full tw-mb-4">
                {/* Name (Optional) */}
                <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                  <label className="leak-label" htmlFor="name">
                    Name{" "}
                    {isPublic && <span className="tw-text-red-500">*</span>}
                    {/* <span className="tw-text-gray-400">
                      (Optional - for anonymity)
                    </span> */}
                  </label>
                  <InputText
                    id="name"
                    value={formValues.name || ""}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        name: e.target.value
                          ? (e.target.value as string).slice(0, 50)
                          : null,
                      })
                    }
                    maxLength={50}
                    // readOnly={!isPublic}
                    disabled={isDisabled}
                    placeholder={`Enter your name`}
                    className="tw-pl-2 tw-border tw-border-gray-300 tw-rounded-md tw-h-11 tw-w-full"
                  />
                </div>

                {/* Date */}
                <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                  <label className="leak-label" htmlFor="date">
                    Date <span className="tw-text-red-500">*</span>
                  </label>
                  <Calendar
                    id="date"
                    value={
                      formValues.date ? new Date(formValues.date * 1000) : null
                    }
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        date: e.value
                          ? convertDateToEpochUTC(e.value as Date)
                          : 0,
                      })
                    }
                    maxDate={new Date()}
                    disabled={isDisabled}
                    dateFormat="dd-mm-yy"
                    showIcon
                    className={`tw-w-full tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal`}
                    inputClassName="tw-w-full"
                  />
                </div>

                {/* Amelio Department */}
                <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                  <label className="leak-label" htmlFor="department">
                    Amelio Department <span className="tw-text-red-500">*</span>
                  </label>
                  <Dropdown
                    id="department"
                    value={formValues.department}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        department: e.value,
                      })
                    }
                    options={departmentOptions}
                    optionLabel="name"
                    disabled={isDisabled}
                    placeholder="Select Amelio Department"
                    filter
                    className="custome-dropdown"
                  />
                </div>

                {/* Observation Type */}
                <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                  <label className="leak-label" htmlFor="observationType">
                    Observation Type <span className="tw-text-red-500">*</span>
                  </label>
                  <Dropdown
                    id="observationType"
                    value={formValues.project}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        project: e.value,
                      })
                    }
                    options={projectNameOptions}
                    optionLabel="name"
                    disabled={isDisabled}
                    placeholder="Select Observation Type"
                    // className="tw-w-full tw-h-11"
                    filter
                    className="custome-dropdown"
                  />
                </div>

                {/* Site Name & Location */}
                <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                  <label className="leak-label" htmlFor="siteLocation">
                    Site Name & Location{" "}
                    <span className="tw-text-red-500">*</span>
                  </label>
                  <InputText
                    id="siteLocation"
                    value={formValues.siteLocation || ""}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        siteLocation: e.target.value
                          ? (e.target.value as string).slice(0, 250)
                          : null,
                      })
                    }
                    maxLength={250}
                    disabled={isDisabled}
                    placeholder="Enter Site Name & Location"
                    className="tw-pl-2 tw-border tw-border-gray-300 tw-rounded-md tw-h-11 tw-w-full"
                  />
                </div>

                {/* Observation */}
                <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                  <label className="leak-label" htmlFor="observation">
                    Observation <span className="tw-text-red-500">*</span>
                  </label>
                  <Dropdown
                    id="observation"
                    value={formValues.observation}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        observation: e.value,
                      })
                    }
                    options={observationTypeOptions}
                    optionLabel="name"
                    disabled={isDisabled}
                    placeholder="Select Observation Type"
                    filter
                    className="custome-dropdown"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="tw-border-b-[1px] tw-border-[#E2E8F0] tw-mb-2 sm:tw-mb-4 tw-pb-4">
              <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                <label className="leak-label" htmlFor="description">
                  Description <span className="tw-text-red-500">*</span>
                </label>
                <InputTextarea
                  id="description"
                  value={formValues.description || ""}
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      description: e.target.value
                        ? (e.target.value as string).slice(0, 400)
                        : null,
                    })
                  }
                  maxLength={400}
                  disabled={isDisabled}
                  placeholder="Enter description"
                  rows={5}
                  className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded-md"
                />
              </div>
            </div>

            {/* Photo Upload - Using PhotoUpload component from Job Sheet Belfast */}
            <div className="tw-border-b-[1px] tw-border-[#E2E8F0] tw-mb-2 sm:tw-mb-4 tw-pb-4">
              <h3 className="tw-text-base tw-font-semibold tw-text-[#101828] tw-mb-4">
                Photos
              </h3>
              <PhotoUpload
                label={<span>Observation Photos (Up to 10 images)</span>}
                name="observation-photos"
                images={images}
                isUploadInProgress={isUploadInProgress}
                setIsUploadInProgress={setIsUploadInProgress}
                setImages={setImages}
                fileValues={fileValues}
                setFileValues={setFileValues}
                timeStampLocationValues={timeStampLocationValues}
                setTimeStampLocationValues={setTimeStampLocationValues}
                classname="tw-w-full"
                maxImages={10}
              />
            </div>

            {/* Control Measures (Optional) */}
            <div className="tw-mb-2 sm:tw-mb-4">
              <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start tw-justify-start">
                <label className="leak-label" htmlFor="controlMeasures">
                  Control Measures{" "}
                  {/* <span className="tw-text-gray-400">(Optional)</span> */}
                </label>
                <InputTextarea
                  id="controlMeasures"
                  value={formValues.controlMeasures || ""}
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      controlMeasures: e.target.value
                        ? (e.target.value as string).slice(0, 250)
                        : null,
                    })
                  }
                  maxLength={250}
                  disabled={isDisabled}
                  placeholder="Enter any control measures"
                  rows={3}
                  className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="tw-flex tw-justify-end tw-items-center tw-p-4 tw-gap-2 !tw-border-t-[#E2E8F0] tw-border-t-[1px]">
            <Button
              className="form-cancel-button"
              label="Cancel"
              disabled={isDisabled}
              onClick={handleCancel}
            />
            <Button
              className="tw-flex tw-px-3 focus:tw-shadow-none tw-py-2 tw-rounded tw-bg-primary tw-border-primary tw-text-white tw-font-medium"
              label={formSubmitting ? "Submitting..." : "Submit"}
              loading={formSubmitting}
              disabled={isDisabled}
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AddObserveInformReport;
